import {
  Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, CloudSnow, CloudSun, Sun,
  type LucideIcon,
} from '../components/ui/Icon';

/**
 * met.no publishes ~100 `symbol_code` values built from a base condition plus a
 * `_day`/`_night`/`_polartwilight` suffix (`lightrainshowersandthunder_night`).
 * Matching them one by one is a maintenance trap — a new variant would fall
 * through to nothing. We match on what the string *contains*, most severe first,
 * which collapses the whole vocabulary into the nine conditions a day chip can
 * usefully show.
 */

/** The palette family a condition paints with. */
export type WeatherTone = 'sun' | 'cloud' | 'fog' | 'rain' | 'snow' | 'storm';

export interface WeatherCondition {
  icon: LucideIcon;
  /** Key under `itinerary.weather.condition` in the locale files. */
  labelKey: string;
  tone: WeatherTone;
}

/**
 * Colour belongs to the *condition* only. Temperature stays teal and rain stays
 * sky everywhere in the weather UI: if the tone painted those too, a sunny day
 * and a stormy one would draw their numbers in different colours and the reader
 * would have to re-learn the legend on every card.
 *
 * Written out in full because Tailwind's scanner only sees literal strings — a
 * class built by interpolation is a class that never ships.
 */
export const WEATHER_TONE: Record<WeatherTone, { icon: string; band: string }> = {
  sun:   { icon: 'text-amber-500 dark:text-amber-400',   band: 'bg-amber-50/70 dark:bg-amber-400/[0.07]' },
  cloud: { icon: 'text-slate-400 dark:text-slate-400',   band: 'bg-slate-50 dark:bg-slate-800/40' },
  fog:   { icon: 'text-slate-400 dark:text-slate-500',   band: 'bg-slate-50 dark:bg-slate-800/40' },
  rain:  { icon: 'text-sky-500 dark:text-sky-400',       band: 'bg-sky-50/70 dark:bg-sky-400/[0.07]' },
  snow:  { icon: 'text-cyan-500 dark:text-cyan-300',     band: 'bg-cyan-50/70 dark:bg-cyan-400/[0.07]' },
  storm: { icon: 'text-violet-500 dark:text-violet-400', band: 'bg-violet-50/70 dark:bg-violet-400/[0.07]' },
};

const RULES: { match: RegExp; condition: WeatherCondition }[] = [
  { match: /thunder/,      condition: { icon: CloudLightning, labelKey: 'thunder',      tone: 'storm' } },
  { match: /snow|sleet/,   condition: { icon: CloudSnow,      labelKey: 'snow',         tone: 'snow' } },
  { match: /lightrain/,    condition: { icon: CloudDrizzle,   labelKey: 'drizzle',      tone: 'rain' } },
  { match: /rain/,         condition: { icon: CloudRain,      labelKey: 'rain',         tone: 'rain' } },
  { match: /fog/,          condition: { icon: CloudFog,       labelKey: 'fog',          tone: 'fog' } },
  // `partlycloudy` before `cloudy`: the loose match would swallow it otherwise.
  { match: /partlycloudy/, condition: { icon: CloudSun,       labelKey: 'partlyCloudy', tone: 'cloud' } },
  { match: /cloudy/,       condition: { icon: Cloud,          labelKey: 'cloudy',       tone: 'cloud' } },
  { match: /fair/,         condition: { icon: CloudSun,       labelKey: 'fair',         tone: 'sun' } },
  { match: /clearsky/,     condition: { icon: Sun,            labelKey: 'clear',        tone: 'sun' } },
];

export function weatherCondition(symbol: string | null): WeatherCondition | null {
  if (!symbol) return null;
  return RULES.find(r => r.match.test(symbol))?.condition ?? null;
}
